/**
 * 部分结账时暂存未勾选商品。
 * Shopify 收银台和购物车是同一份数据，暂存期间不能提前加回去；
 * 结账完成（新空车）或从收银台返回后再加回。
 */
(() => {
  const DEFERRED_KEY = 'theme:cart-deferred-items';
  const UNSELECTED_KEY = 'theme:cart-unselected-lines';
  const LEAVING_KEY = 'theme:cart-checkout-leaving';

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const propertiesFromItem = (item) => {
    const props = item && item.properties;
    if (!props || typeof props !== 'object') return null;
    const cleaned = {};
    Object.keys(props).forEach((key) => {
      const value = props[key];
      if (value == null || value === '') return;
      cleaned[key] = String(value);
    });
    return Object.keys(cleaned).length ? cleaned : null;
  };

  const payloadFromCartItem = (item) => {
    if (!item || !item.variant_id) return null;
    const payload = {
      id: Number(item.variant_id),
      quantity: Number(item.quantity) || 1,
    };
    const properties = propertiesFromItem(item);
    if (properties) payload.properties = properties;
    const sellingPlan =
      item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? item.selling_plan_allocation.selling_plan.id
        : null;
    if (sellingPlan) payload.selling_plan = sellingPlan;
    return payload;
  };

  const propertiesEqual = (left, right) => {
    const a = left && typeof left === 'object' ? left : {};
    const b = right && typeof right === 'object' ? right : {};
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
      const lv = a[key] == null || a[key] === '' ? '' : String(a[key]);
      const rv = b[key] == null || b[key] === '' ? '' : String(b[key]);
      if (lv !== rv) return false;
    }
    return true;
  };

  const sellingPlanId = (item) =>
    (item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
      ? item.selling_plan_allocation.selling_plan.id
      : null) || null;

  const cartHasPayload = (cart, payload) =>
    (cart.items || []).some((item) => {
      if (String(item.variant_id) !== String(payload.id)) return false;
      if ((sellingPlanId(item) || null) !== (payload.selling_plan || null)) return false;
      return propertiesEqual(item.properties, payload.properties);
    });

  const readDeferred = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(DEFERRED_KEY) || 'null');
      if (!parsed || !Array.isArray(parsed.items) || !parsed.items.length) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  };

  const writeDeferred = (data) => {
    try {
      window.localStorage.setItem(DEFERRED_KEY, JSON.stringify(data));
    } catch (error) {
      /* 隐私模式写不了就放弃暂存 */
    }
  };

  const clearDeferred = () => {
    try {
      window.localStorage.removeItem(DEFERRED_KEY);
    } catch (error) {
      /* ignore */
    }
  };

  const clearUnselected = () => {
    try {
      window.localStorage.removeItem(UNSELECTED_KEY);
    } catch (error) {
      /* ignore */
    }
  };

  const markLeaving = () => {
    try {
      window.sessionStorage.setItem(LEAVING_KEY, '1');
    } catch (error) {
      /* ignore */
    }
  };

  const clearLeaving = () => {
    try {
      window.sessionStorage.removeItem(LEAVING_KEY);
    } catch (error) {
      /* ignore */
    }
  };

  const isLeaving = () => {
    try {
      return window.sessionStorage.getItem(LEAVING_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const fromCheckout = () => {
    const referrer = document.referrer || '';
    if (!referrer) return false;
    try {
      return /\/checkouts?(?:\/|$)/i.test(new URL(referrer).pathname);
    } catch (error) {
      return /\/checkouts?/i.test(referrer);
    }
  };

  const navigationType = () => {
    try {
      const entry = performance.getEntriesByType('navigation')[0];
      if (entry && entry.type) return entry.type;
    } catch (error) {
      /* ignore */
    }
    if (performance.navigation && performance.navigation.type === 2) return 'back_forward';
    return '';
  };

  const isBackNavigation = (persisted) => Boolean(persisted) || navigationType() === 'back_forward';

  const onCartPage = () =>
    window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart';

  const shouldShowRestoreLoading = () => onCartPage() && Boolean(readDeferred()) && !isBackNavigation(false);

  const domShowsDeferred = (items) =>
    (items || []).every((payload) => {
      if (!payload || !payload.id) return true;
      return Boolean(document.querySelector(`[data-cart-line][data-variant-id="${payload.id}"]`));
    });

  const endCartOverlay = () => {
    if (!onCartPage()) return;
    document.documentElement.dispatchEvent(new CustomEvent('theme:overlay-loading:end', { bubbles: true }));
  };

  const startCartOverlay = () => {
    if (!onCartPage()) return;
    document.documentElement.dispatchEvent(new CustomEvent('theme:overlay-loading:start', { bubbles: true }));
  };

  const fetchCart = async () => {
    const response = await fetch(`${root()}cart.js`);
    if (!response.ok) throw new Error('cart fetch failed');
    return response.json();
  };

  const addItems = (items) =>
    fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });

  const bundleCart = async () => {
    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );
    const sectionIds = [...sectionsToBundle].filter(Boolean);
    const cart = await fetchCart();
    if (!sectionIds.length) return cart;
    try {
      const sectionsResponse = await fetch(`${root()}?sections=${encodeURIComponent(sectionIds.join(','))}`);
      if (sectionsResponse.ok) cart.sections = await sectionsResponse.json();
    } catch (error) {
      /* 没有 section HTML 时由购物车页自己再拉 */
    }
    return cart;
  };

  const publishCart = (cart, extra) => {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: Object.assign({ baseEvent: 'cart-deferred-restore', cart }, extra || {}),
      })
    );
  };

  let inflight = null;
  let quietUntil = 0;

  const markQuiet = () => {
    quietUntil = Date.now() + 5000;
  };

  const restore = async (options) => {
    if (isLeaving()) return false;
    const deferred = readDeferred();
    if (!deferred) return false;

    const persisted = Boolean(options && options.persisted);
    const fromBack = Boolean(options && options.back) || isBackNavigation(persisted);
    const cart = await fetchCart();
    const toAdd = deferred.items.filter((payload) => payload && payload.id && !cartHasPayload(cart, payload));

    if (!toAdd.length) {
      clearDeferred();
      if (deferred.cartToken && cart.token !== deferred.cartToken) clearUnselected();
      endCartOverlay();
      return false;
    }

    const sameCart = Boolean(deferred.cartToken) && cart.token === deferred.cartToken;
    const allowReturn =
      fromBack ||
      fromCheckout() ||
      Boolean(options && options.scheduled) ||
      (onCartPage() && Date.now() - (deferred.createdAt || 0) > 2000);

    if (sameCart && !allowReturn) {
      if (onCartPage() && !(options && options.scheduled)) {
        const wait = Math.max(2100 - (Date.now() - (deferred.createdAt || 0)), 200);
        window.setTimeout(() => guardedRestore({ scheduled: true }), wait);
      }
      return false;
    }

    const silent = fromBack && (persisted || domShowsDeferred(deferred.items));
    if (!silent && !fromBack && onCartPage()) startCartOverlay();

    let added = false;
    try {
      const response = await addItems(toAdd);
      if (response.ok) {
        added = true;
      } else {
        for (const item of toAdd) {
          const single = await addItems([item]);
          if (single.ok) added = true;
        }
      }

      if (!added) {
        endCartOverlay();
        return false;
      }

      clearDeferred();
      if (!sameCart) clearUnselected();

      if (silent || fromBack) markQuiet();

      window.__themeCartRestorePending = false;

      if (silent) {
        endCartOverlay();
        publishCart(await fetchCart(), { silent: true, quiet: true });
        return true;
      }

      publishCart(await bundleCart(), fromBack ? { quiet: true } : null);
      return true;
    } catch (error) {
      endCartOverlay();
      return false;
    }
  };

  const guardedRestore = (options) => {
    if (inflight) return inflight;
    window.__themeCartRestorePending = true;
    inflight = restore(options)
      .catch(() => false)
      .finally(() => {
        inflight = null;
        window.__themeCartRestorePending = false;
      });
    return inflight;
  };

  window.themeCartDeferred = {
    payloadFromCartItem,
    save(items, meta) {
      if (!items || !items.length) {
        clearDeferred();
        return;
      }
      writeDeferred({
        createdAt: Date.now(),
        cartToken: (meta && meta.cartToken) || '',
        items,
      });
    },
    clear: clearDeferred,
    hasPending: () => Boolean(readDeferred()),
    shouldShowRestoreLoading,
    shouldStayQuiet: () => Date.now() < quietUntil,
    markLeaving,
    clearLeaving,
    restore: guardedRestore,
  };

  window.__themeCartRestorePending = Boolean(readDeferred());

  const startRestore = (options) => {
    clearLeaving();
    return guardedRestore(options);
  };

  if (readDeferred()) startRestore({ back: isBackNavigation(false) });

  window.addEventListener('pageshow', (event) => {
    const back = isBackNavigation(event.persisted);
    if (!back && shouldShowRestoreLoading()) startCartOverlay();
    startRestore({ persisted: event.persisted, back });
  });
})();
